import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Card, Col, Row } from 'react-bootstrap';
import useViewport from 'utils/useViewportHelper';
import useCosmosTrackEvent from 'hooks/useCosmosTrackEvent';
import Spinner from 'components/Spinner';
import useReferrerParam from 'hooks/useReferrerParam';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { RENEWAL } from 'constants/routeConstants';
import { PaymentError } from 'types/expiryRenewal';
import { Store } from 'types/store';
import useFetchPaymentData from './hooks/useFetchPaymentData';
import InfoBlock from './common-components/InfoBlock/InfoBlock';
import DirectDebitForm from './components/organisms/DirectDebitForm';
import PaymentHeader from './components/molecules/PaymentHeader';
import Banner from './components/molecules/Banner';
import PaymentPlan from './components/molecules/PaymentPlan';
import Stepper from './components/organisms/Stepper';

import './expiryRenewalGlobals.scss';
import TimeoutWarningModal from './components/molecules/TimeoutWarningModal';

const Payment = () => {
  const { pageModel, paymentData } = useSelector((state: Store) => state.expiryRenewal);
  const navigate = useNavigate();

  const [error, setError] = useState<PaymentError | null>(null);

  const { isMobile } = useViewport();

  const isAppView = useReferrerParam();

  const { loading } = useFetchPaymentData({ setErrorModal: setError });

  const { cosmosTrackEvent } = useCosmosTrackEvent('ExpiryRenewals');

  useEffect(() => {
    cosmosTrackEvent('renewal_journey_directdebit');
  }, []);

  const isMonthly = pageModel?.isMonthlyLongerTermPolicy;
  const monthlyHeadlinePriceNow = paymentData?.basketContent?.monthlyHeadlinePriceNow || null;
  const monthlyHeadlinePriceWas = paymentData?.basketContent?.monthlyHeadlinePriceWas || null;
  const firstInstalmentPrice = paymentData?.basketContent?.firstInstalmentPrice || null;
  const remainingInstalmentPrice = paymentData?.basketContent?.remainingInstalmentPrice || null;
  const price = paymentData?.price || null;
  const discountedPrice = paymentData?.discountModel?.originalPrice || null;
  const isDiscountedPrice = paymentData?.discountModel?.isDiscountedPrice || null;
  /* Logic to hide "was" price when it equals "now" price */
  const hideMonthlyHeadlinePriceWas = monthlyHeadlinePriceNow === monthlyHeadlinePriceWas;
  const monthlyPriceWas = hideMonthlyHeadlinePriceWas ? null : monthlyHeadlinePriceWas;
  const annualPriceWas = isDiscountedPrice && discountedPrice;

  useEffect(() => {
    if (!pageModel) navigate(RENEWAL, { replace: true });
  }, [pageModel, navigate]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center bg-grey">
        {isMobile ? <Spinner mobileSpinner /> : <Spinner bootSpinner />}
      </div>
    );
  }

  return (
    <>
      {pageModel?.discountModel?.isDiscountedPrice && (
        <Banner />
      )}
      <Stepper step={4} />
      <div className={clsx('fs-6 mb-4 text-secondary', isAppView ? 'm-3 p-3 bg-white border rounded' : 'p-4')}>
        {pageModel && (
          <PaymentHeader
            smallHeader={isMonthly ? 'Pay monthly' : 'Pay annually'}
            mainHeader="Set up your direct debit"
            description={isMonthly && 'We can’t always split the cost of your cover into equal monthly payments.'}
            price={isMonthly ? monthlyHeadlinePriceNow : price}
            priceWas={isMonthly ? monthlyPriceWas : annualPriceWas}
          />
        )}
        {isMonthly && firstInstalmentPrice && remainingInstalmentPrice && (
          <PaymentPlan data={{
            isDirectDebit: true,
            items: [
              {
                title: 'Next payment',
                amount: firstInstalmentPrice,
              },
              {
                title: 'Remaining payments',
                amount: remainingInstalmentPrice,
              },
            ],
          }}
          />
        )}
        <Card className="border border-light shadow-sm bg-body rounded">
          <Row>
            <Col>
              {error?.message && error?.title ? (
                <InfoBlock header={error?.title} variant="danger" showIcon>
                  <span className="fw-bold text-danger">
                    {error?.message}
                  </span>
                </InfoBlock>
                ) : (
                  <DirectDebitForm />
                )}
            </Col>
          </Row>
        </Card>
      </div>
      <TimeoutWarningModal />
    </>
  );
}

export default Payment;
